import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Card, Button, Alert, Spinner,
} from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';
import Header from '~/Components/Header/Header';
import ModalConfirmacao from '~/Components/ModalConfirmacao';
import ModalDespesaCartaoCredito from '~/Pages/DespesaCartaoCredito/components/ModalDespesaCartaoCredito';
import * as api from '~/Services/Service';
import FiltrosFechamento from './components/FiltrosFechamento';
import GridDespesasElegiveis from './components/GridDespesasElegiveis';
import PainelLancaChave from './components/PainelLancaChave';

const FechamentoCartaoCredito = () => {
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const cartaoInicial = queryParams.get('cartao') ? Number(queryParams.get('cartao')) : null;

  const [filtros, setFiltros] = useState({
    cartao: cartaoInicial,
    vencimento_inicio: null,
    vencimento_fim: null,
    data_compra_inicio: null,
    data_compra_fim: null,
    mostrar_ja_utilizadas: false,
  });

  const [opcoesCartao, setOpcoesCartao] = useState([]);
  const [cartoesFull, setCartoesFull] = useState([]);
  const [opcoesGE, setOpcoesGE] = useState([]);
  const [loadingCartoes, setLoadingCartoes] = useState(false);
  const [modoModalDespesa, setModoModalDespesa] = useState(null);

  const [elegiveis, setElegiveis] = useState([]);
  const [selecionados, setSelecionados] = useState([]);
  const [temMoedaEstrangeira, setTemMoedaEstrangeira] = useState(false);
  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [buscaFeita, setBuscaFeita] = useState(false);
  const [codigoGeElegiveis, setCodigoGeElegiveis] = useState(null);

  // D-F-01 — valores de IOF/Cotação por despesa (pas:353-389 edtCotacao/edtIOF por linha).
  // Mapa: { [codigo]: { cotacao: string, iof: string } } — strings para inputs controlados.
  const [valoresIofPorDespesa, setValoresIofPorDespesa] = useState({});
  const [ordenacao, setOrdenacao] = useState({ campo: 'vencimento', direcao: 'asc' });
  const [paginaElegiveis, setPaginaElegiveis] = useState(1);
  const ITENS_POR_PAGINA = 50;

  const [mostrarLancaChave, setMostrarLancaChave] = useState(false);
  const [valoresLancaChave, setValoresLancaChave] = useState(null);
  const [showConfirmacao, setShowConfirmacao] = useState(false);
  // uFechamentoCartaoCredito.pas:1033-1042 — modal fMensagem "Anexar agora / Anexar depois"
  // quando nenhum PDF foi carregado antes do Gravar.
  const [showAnexarDepois, setShowAnexarDepois] = useState(false);
  const [loadingGravar, setLoadingGravar] = useState(false);

  useEffect(() => {
    carregarCartoes();
    carregarGEs();
  }, []);

  const carregarCartoes = async () => {
    setLoadingCartoes(true);
    try {
      const data = await api.listarCartoesCredito(null);
      const lista = data || [];
      setCartoesFull(lista);
      setOpcoesCartao(lista.map((c) => {
        const sufixo = `${c.ultimo_numero_cartao || ''} - ${c.bandeira || ''}`.replace(/^ - | - $/g, '').trim();
        return {
          value: c.codigo_cartao,
          label: sufixo ? `${c.codigo_cartao} - ${sufixo}` : String(c.codigo_cartao),
        };
      }));
    } catch (e) {
      setCartoesFull([]);
      setOpcoesCartao([]);
    } finally {
      setLoadingCartoes(false);
    }
  };

  const carregarGEs = async () => {
    try {
      const ges = await api.listarGruposEmpresaLancamentoDespesaCartao();
      setOpcoesGE((ges || []).map((g) => ({
        value: g.codigo_grupo_empresa,
        label: g.apelido
          ? `${g.codigo_grupo_empresa} - ${g.apelido}`
          : String(g.codigo_grupo_empresa),
      })));
    } catch (e) {
      setOpcoesGE([]);
    }
  };

  const handleBuscar = useCallback(async () => {
    if (!filtros.cartao) return;
    setLoadingBuscar(true);
    setElegiveis([]);
    setSelecionados([]);
    setTemMoedaEstrangeira(false);
    setMostrarLancaChave(false);
    setBuscaFeita(false);
    setCodigoGeElegiveis(null);
    try {
      const res = await api.buscarDespesasElegiveis(filtros);
      const lista = Array.isArray(res) ? res : (res.itens || []);
      const temEstrangeira = res && !Array.isArray(res) ? (res.tem_moeda_estrangeira || false) : false;
      const geCode = lista.length > 0 && lista[0].codigo_grupo_empresa ? lista[0].codigo_grupo_empresa : null;
      setElegiveis(lista);
      setTemMoedaEstrangeira(temEstrangeira);
      setCodigoGeElegiveis(geCode);
      setValoresIofPorDespesa({});
      setBuscaFeita(true);
      setPaginaElegiveis(1);
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao buscar despesas elegíveis';
      toast.error(msg);
    } finally {
      setLoadingBuscar(false);
    }
  }, [filtros]);

  const handleToggleSelecionado = (codigo) => {
    setSelecionados((prev) =>
      prev.includes(codigo) ? prev.filter((c) => c !== codigo) : [...prev, codigo]
    );
  };

  const handleToggleTodos = (codigosPagina, todosMarcados) => {
    if (!Array.isArray(codigosPagina)) {
      // fallback: seleciona/desseleciona tudo (se chamado sem args)
      setSelecionados(selecionados.length === elegiveis.length ? [] : elegiveis.map((d) => d.codigo));
      return;
    }
    setSelecionados((prev) => {
      if (todosMarcados) {
        return prev.filter((c) => !codigosPagina.includes(c));
      }
      return Array.from(new Set([...prev, ...codigosPagina]));
    });
  };

  const handleOrdenar = (campo) => {
    setOrdenacao((prev) => ({
      campo,
      direcao: prev.campo === campo && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const elegiveisOrdenados = [...elegiveis].sort((a, b) => {
    const dir = ordenacao.direcao === 'asc' ? 1 : -1;
    const va = a[ordenacao.campo];
    const vb = b[ordenacao.campo];
    if (va == null) return dir;
    if (vb == null) return -dir;
    if (typeof va === 'number') return dir * (va - vb);
    return dir * String(va).localeCompare(String(vb));
  });

  const selecionadosData = elegiveis.filter((d) => selecionados.includes(d.codigo));
  const totalSelecionado = selecionadosData.reduce((acc, d) => acc + (Number(d.valor) || 0), 0);

  const handleGravar = () => {
    if (selecionados.length === 0) {
      toast.warning('Selecione ao menos uma despesa para fechar');
      return;
    }
    setMostrarLancaChave(true);
  };

  const handleConfirmarLancaChave = (values) => {
    setValoresLancaChave(values);
    // Paridade Delphi fMensagem (pas:1033-1042): sem PDF, exigir escolha explícita.
    if (!values.arquivo_pdf) {
      setShowAnexarDepois(true);
      return;
    }
    setShowConfirmacao(true);
  };

  const handleConfirmarFechamento = async () => {
    setShowConfirmacao(false);
    setLoadingGravar(true);
    try {
      const {
        arquivo_pdf,
        fornecedor_raw,
        departamento_raw,
        ...camposLancaChave
      } = valoresLancaChave;
      const camposNormalizados = Object.keys(camposLancaChave).reduce((acc, k) => {
        const v = camposLancaChave[k];
        acc[k] = v === '' ? null : v;
        return acc;
      }, {});
      // D-F-01 — monta valores_iof apenas para despesas selecionadas em moeda estrangeira.
      const valoresIof = selecionadosData
        .filter((d) => d.moeda && d.moeda !== 'R')
        .map((d) => {
          const v = valoresIofPorDespesa[d.codigo] || {};
          return {
            codigo_despesa: d.codigo,
            cotacao: v.cotacao !== '' && v.cotacao != null ? Number(v.cotacao) : 0,
            iof: v.iof !== '' && v.iof != null ? Number(v.iof) : 0,
          };
        });
      const faltaCotacao = valoresIof.find((v) => !(v.cotacao > 0));
      if (faltaCotacao) {
        toast.warning(`Informe Cotação > 0 para a despesa ${faltaCotacao.codigo_despesa} (moeda estrangeira).`);
        setLoadingGravar(false);
        return;
      }
      const dados = {
        codigos_despesa: selecionados,
        codigo_grupo_empresa: geDoCartaoSelecionado,
        cartao: filtros.cartao,
        valores_iof: valoresIof,
        anexar_depois: !arquivo_pdf,
        ...camposNormalizados,
      };
      const res = await api.gravarFechamentoDespesas(dados, arquivo_pdf);
      const seq = res && (res.seq_cap != null ? res.seq_cap : (res.sequencia != null ? res.sequencia : res.codigo));
      toast.success(`Fechamento realizado${seq != null ? ` — Sequência ${seq}` : ''}!`);
      setMostrarLancaChave(false);
      setSelecionados([]);
      setValoresLancaChave(null);
      handleBuscar();
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao gravar fechamento';
      toast.error(msg);
    } finally {
      setLoadingGravar(false);
    }
  };

  const mensagemConfirmacao = `Confirma fechamento de ${selecionados.length} despesa(s) totalizando ${totalSelecionado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}?`;

  const geDoCartaoSelecionado = (() => {
    if (!filtros.cartao) return null;
    const cartao = cartoesFull.find((c) => c.codigo_cartao === filtros.cartao);
    return cartao ? cartao.codigo_grupo_empresa : null;
  })();

  const handleLancarNovaDespesa = () => {
    setModoModalDespesa('create');
  };

  const handleDespesaSalva = async (resposta) => {
    setModoModalDespesa(null);
    const novosCodigos = (resposta && resposta.codigos_criados) || [];
    if (!filtros.cartao) return;
    try {
      const res = await api.buscarDespesasElegiveis(filtros);
      const lista = Array.isArray(res) ? res : (res.itens || []);
      const temEstrangeira = res && !Array.isArray(res) ? (res.tem_moeda_estrangeira || false) : false;
      const geCode = lista.length > 0 && lista[0].codigo_grupo_empresa ? lista[0].codigo_grupo_empresa : null;
      setElegiveis(lista);
      setTemMoedaEstrangeira(temEstrangeira);
      setCodigoGeElegiveis(geCode);
      setBuscaFeita(true);
      if (novosCodigos.length > 0) {
        const idsNaLista = lista.map((d) => d.codigo);
        const pre = novosCodigos.filter((c) => idsNaLista.includes(c));
        if (pre.length > 0) {
          setSelecionados((prev) => Array.from(new Set([...prev, ...pre])));
        }
      }
    } catch (err) {
      const msg = err && err.message ? err.message : 'Erro ao atualizar despesas elegíveis';
      toast.error(msg);
    }
  };

  return (
    <>
      <Header title="Fechamento de Despesa de Cartão de Crédito" />
      <Container fluid className="px-3 py-3">
        <Card className="shadow-sm mb-3">
        <Card.Body>
          <FiltrosFechamento
            filtros={filtros}
            onChange={setFiltros}
            onBuscar={handleBuscar}
            opcoesCartao={opcoesCartao}
            loading={loadingBuscar || loadingCartoes}
          />
        </Card.Body>
      </Card>

      {loadingBuscar && (
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
          <span className="ml-2">Buscando despesas elegíveis...</span>
        </div>
      )}

      {buscaFeita && !loadingBuscar && (
        <>
          {selecionados.length > 0 && (
            <Alert variant="info" className="mb-3">
              <strong>{selecionados.length}</strong> despesa(s) selecionada(s) —{' '}
              Total:{' '}
              <strong>
                {totalSelecionado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </strong>
            </Alert>
          )}

          <Card className="shadow-sm mb-3">
            <Card.Header
              className="py-2 d-flex justify-content-between align-items-center"
              style={{ backgroundColor: '#0747a6', color: '#fff' }}
            >
              <strong>
                Despesas Elegíveis
                <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                  ({elegiveis.length} {elegiveis.length === 1 ? 'registro' : 'registros'})
                </span>
              </strong>
            </Card.Header>
            <Card.Body className="p-2">
              <GridDespesasElegiveis
                despesas={elegiveisOrdenados}
                selecionados={selecionados}
                onToggleSelecionado={handleToggleSelecionado}
                onToggleTodos={handleToggleTodos}
                ordenacao={ordenacao}
                onOrdenar={handleOrdenar}
                pagina={paginaElegiveis}
                itensPorPagina={ITENS_POR_PAGINA}
                onChangePagina={setPaginaElegiveis}
                valoresIofPorDespesa={valoresIofPorDespesa}
                onChangeValoresIof={(codigo, campo, valor) =>
                  setValoresIofPorDespesa((prev) => ({
                    ...prev,
                    [codigo]: { ...(prev[codigo] || {}), [campo]: valor },
                  }))
                }
              />
            </Card.Body>
          </Card>

          <div className="text-right mb-3 d-flex justify-content-end" style={{ gap: 8 }}>
            <Button
              variant="success"
              onClick={handleLancarNovaDespesa}
              disabled={!filtros.cartao}
            >
              <FaPlus className="mr-1" /> Lançar Nova Despesa
            </Button>
            <Button
              variant="primary"
              disabled={selecionados.length === 0}
              onClick={handleGravar}
            >
              Gravar Fechamento
            </Button>
          </div>

          <PainelLancaChave
            show={mostrarLancaChave}
            onClose={() => setMostrarLancaChave(false)}
            codigoGe={codigoGeElegiveis}
            onConfirmar={handleConfirmarLancaChave}
            loadingGravar={loadingGravar}
            totalSelecionado={totalSelecionado}
          />
        </>
      )}

      {modoModalDespesa && (
        <ModalDespesaCartaoCredito
          show={!!modoModalDespesa}
          modo={modoModalDespesa}
          codigo={null}
          geDefault={geDoCartaoSelecionado}
          opcoesGE={opcoesGE}
          onClose={() => setModoModalDespesa(null)}
          onSalvo={handleDespesaSalva}
        />
      )}

      <ModalConfirmacao
        show={showConfirmacao}
        title="Confirmar Fechamento"
        message={mensagemConfirmacao}
        onClose={(confirmado) => {
          if (confirmado) {
            handleConfirmarFechamento();
          } else {
            setShowConfirmacao(false);
          }
        }}
      />

      <ModalConfirmacao
        show={showAnexarDepois}
        title="Anexo da Fatura"
        message={'Nenhum PDF foi anexado. Deseja continuar e anexar depois? Clique em OK para anexar depois ou Cancelar para voltar e anexar agora.'}
        onClose={(anexarDepois) => {
          setShowAnexarDepois(false);
          if (anexarDepois) {
            setShowConfirmacao(true);
          }
        }}
      />
      </Container>
    </>
  );
};

export default FechamentoCartaoCredito;
